import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Filter, SlidersHorizontal } from "lucide-react";

const CAT_NAMES = {
  telephones: "Téléphones & Smartphones",
  audio: "Audio & Casques",
  electromenager: "Électroménager",
  "energie-solaire": "Énergie solaire",
  batteries: "Batteries & stockage",
  nouveautes: "Nouveautés",
  "meilleures-ventes": "Meilleures ventes",
  promotions: "Promotions",
};

export default function ProductListPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [maxPrice, setMaxPrice] = useState("");
  const [minPrice, setMinPrice] = useState("");

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (search) q.search = search;
    if (sort) q.sort = sort;
    if (minPrice) q.min_price = Number(minPrice);
    if (maxPrice) q.max_price = Number(maxPrice);
    api.get("/products", { params: q })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, search, sort, minPrice, maxPrice]);

  const title = search
    ? `Résultats pour "${search}"`
    : (CAT_NAMES[category] || "Tous les produits");

  return (
    <div className="je-container py-8" data-testid="product-list-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold" style={{fontFamily:"Outfit"}}>{title}</h1>
          <p className="text-sm text-gray-500 mt-1">{products.length} produit(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-gray-500" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white" data-testid="sort-select">
            <option value="newest">Plus récents</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
            <option value="rating">Mieux notés</option>
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-44 self-start bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-4"><Filter className="w-4 h-4" /> Filtres</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500 tracking-wider">Prix (HTG)</label>
              <div className="flex gap-2 mt-2">
                <input type="number" placeholder="Min" value={minPrice} onChange={(e)=>setMinPrice(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="min-price-input" />
                <input type="number" placeholder="Max" value={maxPrice} onChange={(e)=>setMaxPrice(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="max-price-input" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500 tracking-wider">Catégorie</label>
              <div className="mt-2 space-y-1.5">
                {Object.entries(CAT_NAMES).map(([slug, name]) => (
                  <button
                    key={slug}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      if (category === slug) next.delete("category"); else next.set("category", slug);
                      setParams(next);
                    }}
                    className={`flex items-center gap-2 text-left text-sm w-full px-2 py-1 rounded ${category===slug ? "bg-[#0052CC] text-white" : "hover:bg-gray-100 text-gray-700"}`}
                    data-testid={`filter-cat-${slug}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${category===slug ? "bg-white" : "bg-[#E3000F]"}`} />
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({length: 6}).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-gray-100 animate-pulse rounded-xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded-xl">
              <p className="text-gray-500">Aucun produit trouvé.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="product-grid">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
